/* eslint-disable react/jsx-props-no-spreading */
import { useIsVisible } from '@folklore/hooks';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import React, { Suspense, useMemo } from 'react';

import usePlaceholderItems from '../../hooks/usePlaceholderItems';

import { useIsIdle } from '../../contexts/IdleContext';
import { useIsCapture } from '../../contexts/LayoutContext';
import ItemsLoader from './ItemsLoader';
import List from './List';
import * as ListPresentations from './presentations';

export interface PresentationListProps {
    hook: (...args: any[]) => { items?: any[]; isLoading: boolean; isError: boolean };
    type?: string | null;
    title?: React.ReactNode;
    count?: number | null;
    query?: Record<string, unknown> | null;
    disabled?: boolean;
    initialData?: any[] | null;
    items?: any[] | null;
    suspense?: boolean;
    loading?: null | 'lazy';
    presentation?: 'grid' | 'row' | 'layout' | 'inline';
    paginated?: boolean;
    placeholderItem?: Record<string, unknown> | null;
    placeholderCount?: number | null;
    withoutLoader?: boolean;
    className?: string | null;
    rowClassName?: string | null;
    gridClassName?: string | null;
    onLoaded?: () => void;
    [key: string]: unknown;
}

function PresentationList({
    hook,
    presentation = 'grid',
    count = null,
    query = null,
    disabled = false,
    initialData = null,
    items = null,
    suspense = false,
    loading = null,
    paginated = true,
    placeholderItem = null,
    placeholderCount = null,
    withoutLoader = false,
    className = null,
    rowClassName = null,
    gridClassName = null,
    onLoaded = null,
    ...props
}: PresentationListProps) {
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;

    const placeholderItems = usePlaceholderItems(placeholderCount || count || 3, placeholderItem);
    const finalQuery = useMemo(
        () => ({
            count,
            ...query,
        }),
        [count, query],
    );

    const isIdle = useIsIdle();

    const finalPresentation = presentation || 'grid';
    const finalDisabled = disabled || !shouldLoad || isIdle;

    const ListComponent = getComponentFromName(ListPresentations, finalPresentation, List);
    const list = (
        <ListComponent
            {...props}
            ref={ref}
            className={classNames([
                {
                    [className]: className !== null,
                    [gridClassName]: gridClassName !== null && finalPresentation === 'grid',
                    [rowClassName]: rowClassName !== null && finalPresentation === 'row',
                },
            ])}
        />
    );

    return withoutLoader ? (
        React.cloneElement(list, {
            items: items || placeholderItems,
        })
    ) : (
        <Suspense
            fallback={React.cloneElement(list, {
                items: placeholderItems,
            })}
        >
            <ItemsLoader
                hook={hook}
                query={finalQuery}
                suspense={suspense}
                disabled={finalDisabled}
                // keepPreviousData={keepPreviousData}
                initialData={initialData}
                placeholderData={
                    paginated
                        ? {
                              data: placeholderItems,
                          }
                        : placeholderItems
                }
                onLoaded={onLoaded}
            >
                {list}
            </ItemsLoader>
        </Suspense>
    );
}

export default PresentationList;
